import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import 'react-dates/initialize';
import 'components/Shared/CustomFilter.css'
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker } from 'react-dates';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { useGridsState } from 'hooks/useGridsState';
import moment from 'moment';
import { useAppContext } from 'hooks/useAppContext';

/**
 * Custom hook to manage updates to gridsContext. It debounces the updates to wait until the user finishes typing before sending the state update to context.
 * @param {*} initialValue the initial value of the 'value' that is returned
 * @param {*} props the props of the CustomFilter component
 * @param {*} columnDef the columnDef of the column
 * @returns 
 */
const useValueDebouncer = (initialValue, props, columnDef) => {
  const ref = React.useRef("");
  const { updateGridFilter } = useGridsState();
  const [value, setValue] = useState(initialValue);

  const onChange = (val) => {

    // set ref.current to store current value;
    ref.current = val;

    // set local component state
    setValue(val);

    // debounce the gridState context update; in case the user is still typing
    setTimeout(() => {
      // check val against ref to see if its still valid; if so; update context;
      if (val === ref.current) {
        updateGridFilter({
          gridTitle: props.title,
          filters: {
            [columnDef.field]: { filter: val }
          }
        })
      } else {
        return
      }
    }, 1000);
  };

  return [value, onChange]
};

export const useGridsFilterValue = (gridTitle, field) => {
  const { gridStates } = useGridsState();
  let { selectedProject } = useAppContext();
  if(!selectedProject) {
    selectedProject = "@global";
  }
  let value = '';
  try {
    value = gridStates[selectedProject][gridTitle].filters[field].filter;
    return value;
  } catch (e) {
    return value;
  }
}

/**
 * custom date-range filter, that hooks into GridsState
 * @param {object} props 
 * @param {object} props.columnDef comes from material-grid
 * @param {string} props.title comes from material grid and is passed to here from CustomFilter
 * @returns 
 */
const FilterDateRange = (props) => {
  const { columnDef, onFilterChanged, title } = props;

  const [dates, setDates] = useState({
    startDate: (columnDef.defaultFilter !== "" && columnDef.defaultFilter) ? moment(columnDef.defaultFilter.ge) : null,
    endDate: (columnDef.defaultFilter !== "" && columnDef.defaultFilter) ? moment(columnDef.defaultFilter.le) : null
  });

  const currentFilterVal = useGridsFilterValue(title, columnDef.field);

  const [focusedInput, setFocusedInput] = useState(null);

  const [, onChange] = useValueDebouncer(null, props, columnDef)

  useEffect(() => {
    //some ugly logic here... but we need to make sure if there are dates, that they are different. and if there are no pre-filter dates, we set them
    if (dates.startDate &&
      dates.endDate &&
      (((currentFilterVal && dates.startDate.diff(currentFilterVal.ge) !== 0) || !currentFilterVal) ||
      ((currentFilterVal && dates.endDate.diff(currentFilterVal.le) !== 0) || !currentFilterVal))) {
      const dateFilterValue = {
        ge: moment.utc(dates.startDate.format('YYYY-MM-DD')).startOf('day').toDate(),
        le: moment.utc(dates.endDate.format('YYYY-MM-DD')).endOf('day').toDate()
      };
      onChange(dateFilterValue);
      onFilterChanged(columnDef.tableData.id, dateFilterValue);
    }
    if (!dates.startDate &&
      !dates.endDate &&
      (currentFilterVal && currentFilterVal.ge !== null) &&
      (currentFilterVal && currentFilterVal.le !== null)) {
      onChange(null);
      onFilterChanged(columnDef.tableData.id, null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  return (
    <DateRangePicker
      startDate={dates.startDate} // momentPropTypes.momentObj or null,
      startDateId={Math.random(10).toString()} // PropTypes.string.isRequired,
      endDate={dates.endDate} // momentPropTypes.momentObj or null,
      endDateId={Math.random(10).toString()} // PropTypes.string.isRequired,
      onDatesChange={({ startDate, endDate }) => setDates({ startDate, endDate })} // PropTypes.func.isRequired,
      focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
      onFocusChange={(e) => { setFocusedInput(e) }} // PropTypes.func.isRequired,
      showClearDates={true}
      displayFormat="M/D/YY"
      withPortal={true}
      isOutsideRange={(day) => false}
    />
  )
}

const FilterGuid = ({ columnDef, onFilterChanged }) => {
  const [val, setVal] = useState('');
  const isError = (v) => v.length < 36 && v.length !== 0;
  return (
    <TextField
      error={isError(val)}
      helperText={isError(val) ? "Must Enter Full Id" : null}
      type="search"
      onChange={(event) => {
        onFilterChanged(columnDef.tableData.id, event.target.value);
        setVal(event.target.value);
      }}
    />
  )
}

const FilterBool = (props) => {

  const { columnDef, title } = props;

  const initialValue = useGridsFilterValue(title, columnDef.field);

  const [value, onChange] = useValueDebouncer(initialValue, props, columnDef);

  const handleChange = (e) => {
    const v = e.target.value;
    if (typeof (v) === "number") {
      onChange(v);
    } else {
      onChange(undefined);
    }
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      displayEmpty
      style={{ width: "100%" }}
    >
      <MenuItem value={''}>
        <em>None</em>
      </MenuItem>
      <MenuItem value={2}>Checked</MenuItem>
      <MenuItem value={1}>Unchecked</MenuItem>
    </Select>
  )
}

const CustomFilter = (props) => {
  const { columnDef, onFilterChanged, title } = props;

  const initialValue = useGridsFilterValue(title, columnDef.field);

  const [value, onChange] = useValueDebouncer(initialValue, props, columnDef);

  const onValueChange = (event) => {
    const val = event.target.value;
    onChange(val);
    // Trigger MaterialTable to refetch data with the new filter
    onFilterChanged(columnDef.tableData.id, val);
  }

  return (
    <>
      {
        (() => {
          if (typeof (columnDef.filter) === "boolean" && columnDef.filter === false) {
            return (
              <TextField
                disabled
                placeholder="NA"
              ></TextField>
            )
          } else {
            switch (columnDef.type) {
              case "date":
                return <FilterDateRange columnDef={columnDef} onFilterChanged={onFilterChanged} title={title} />;
              case "boolean":
                return <FilterBool columnDef={columnDef} onFilterChanged={onFilterChanged} title={title} />;
              case "guid":
                return <FilterGuid columnDef={columnDef} onFilterChanged={onFilterChanged} title={title} />;
              default:
                return (
                  <TextField
                    style={columnDef.type === 'numeric' ? { float: 'right' } : {}}
                    type={columnDef.type === 'numeric' ? 'number' : 'search'}
                    value={/*(!!gridStates[props.title] && !!gridStates[props.title][columnDef.field]) ? gridStates[props.title][columnDef.field].filter : "" */value}
                    onChangeCapture={onValueChange}
                  />
                )
            };
          }
        })()
      }
    </>
  )
}

export default CustomFilter;